import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  Car,
  MessageCircle,
  Instagram,
  Mail,
  CheckCircle2,
} from "lucide-react";
import dayjs from "dayjs";

import { get, ref } from "firebase/database";

import { database } from "../firebase/firebase";
import LoadingScreen from "../utils/LoadingScreen";
import black_logo from "../assets/images/black_logo.png";
import BrazilianPlate from "../components/BrazilianPlate";
import { Button } from "../components/ui/button";
import { useCatalog } from "../contexts/CatalogContext";

const contactLinks = [
  {
    icon: <MessageCircle className="h-4 w-4" />,
    href: "https://wa.me/5548996523066",
    label: "WhatsApp",
  },
  {
    icon: <Instagram className="h-4 w-4" />,
    href: "https://www.instagram.com/transfer.onfloripa/",
    label: "Instagram",
  },
  {
    icon: <Mail className="h-4 w-4" />,
    href: "mailto:transfer.onfloripa@gmail.com",
    label: "Email",
  },
  {
    icon: <Phone className="h-4 w-4" />,
    href: "tel://5548996523066",
    label: "Telefone",
  },
];

export default function CheckinPage() {
  const [booking, setBooking] = useState<any>();
  const [driver, setDriver] = useState<any>();
  const [vehicle, setVehicle] = useState<any>();

  const { driversMap, vehiclesMap } = useCatalog();

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/");
      return;
    }
    get(ref(database, `booking/${id}`)).then((snapshot) => {
      if (snapshot.exists()) {
        setBooking(snapshot.val());
      } else {
        navigate("/");
      }
    });
  }, [id, navigate]);

  useEffect(() => {
    if (!booking) return;
    if (booking.driver && driversMap[booking.driver]) {
      setDriver(driversMap[booking.driver]);
    }
    if (booking.vehicle && vehiclesMap[booking.vehicle]) {
      setVehicle(vehiclesMap[booking.vehicle]);
    }
  }, [booking, driversMap, vehiclesMap]);

  if (!booking || !driver || !vehicle) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-8 md:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block">
            <img
              className="mx-auto h-12 w-auto"
              src={black_logo}
              alt="Transfer On Floripa"
            />
          </Link>
        </div>

        {/* Ticket Card */}
        <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl">
          {/* Confirmation Badge */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-white">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                Instruções de Embarque
              </span>
            </div>
          </div>

          {/* Ticket Content */}
          <div className="p-6 md:p-8">
            {/* Welcome Message */}
            <div className="mb-6 border-b border-slate-200 pb-6 text-center">
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {booking.name}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Seu motorista está confirmado e pronto para te buscar!
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
                <Calendar className="h-4 w-4" />
                <span>{dayjs(booking.date).format("DD/MM/YYYY")}</span>
                <Clock className="ml-3 h-4 w-4" />
                <span>{dayjs(booking.date).format("HH:mm")}</span>
              </div>
            </div>

            {/* Driver Section */}
            <div className="mb-6 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
              <div className="mb-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Seu Motorista
                </p>
              </div>
              <div className="flex flex-col items-center gap-4 md:flex-row md:items-start">
                <div className="flex-shrink-0">
                  <img
                    src={driver.photo}
                    alt={driver.name}
                    className="h-24 w-24 rounded-full border-4 border-white object-cover shadow-lg"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-bold text-slate-900">
                    {driver.name}
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Motorista profissional com experiência em transfers
                    executivos
                  </p>
                </div>
              </div>
            </div>

            {/* Dotted Separator */}
            <div className="my-6 flex items-center gap-2">
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
              <div className="h-2 w-2 rounded-full bg-slate-400"></div>
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
            </div>

            {/* Vehicle Section */}
            <div className="mb-6">
              <div className="mb-4 text-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Veículo
                </p>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <div className="relative h-48 w-full overflow-hidden md:h-56">
                  <img
                    src={vehicle.photo}
                    alt={vehicle.name}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="p-4 md:p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-900">
                      {vehicle.name}
                    </h3>
                    <p className="text-sm text-slate-600">{vehicle.color}</p>
                  </div>

                  {/* Placa destacada */}
                  <div className="mb-6 flex items-center justify-center">
                    <BrazilianPlate plate={vehicle.plate} />
                  </div>

                  <div className="flex items-center gap-2 rounded-lg bg-white p-3 text-sm text-slate-700 shadow-sm">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    <span className="font-medium">{booking.origin}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-medium">{booking.destination}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dotted Separator */}
            <div className="my-6 flex items-center gap-2">
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
              <div className="h-2 w-2 rounded-full bg-slate-400"></div>
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
            </div>

            {/* Additional Info */}
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <Phone className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">
                      Seu Telefone
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      {booking.phone}
                    </p>
                  </div>
                </div>
              </div>

              {booking.flight && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100">
                      <Car className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500">Voo</p>
                      <p className="text-sm font-semibold text-slate-900">
                        {booking.flight}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dotted Separator */}
            <div className="my-6 flex items-center gap-2">
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
              <div className="h-2 w-2 rounded-full bg-slate-400"></div>
              <div className="flex-1 border-t-2 border-dashed border-slate-300"></div>
            </div>

            {/* Contact Section */}
            <div className="mb-6">
              <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                Precisa de Ajuda?
              </p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {contactLinks.map((link) => (
                  <Button
                    key={link.label}
                    asChild
                    variant="outline"
                    className="h-auto flex-col gap-2 border-slate-300 py-3 text-slate-700 hover:bg-slate-50"
                  >
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex flex-col items-center gap-2"
                    >
                      {link.icon}
                      <span className="text-xs font-medium">{link.label}</span>
                    </a>
                  </Button>
                ))}
              </div>
            </div>

            {/* Important Notice */}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-center text-xs font-medium text-amber-900">
                <strong>Importante:</strong> Esteja no local de embarque com{" "}
                <strong>10 minutos de antecedência</strong>. Em caso de dúvidas,
                entre em contato pelo WhatsApp.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          Desenvolvido por{" "}
          <a
            href="https://richardpompeo.com.br"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-slate-700 hover:text-slate-900"
          >
            Richard Pompeo
          </a>
        </p>
      </div>
    </div>
  );
}
